import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { invalidateCruiseCache } from '../hooks/useCruiseSettings';
import { NAVY, GOLD } from '../utils/constants';
import { Clock, ClipboardList, AlertTriangle, Globe } from 'lucide-react';
import { TimeField, pad2 } from './TimeField';
import { CruiseTimesSchedule, todayIso } from './ScheduledChanges';

const isDinner = (name) => (name || '').toLowerCase().includes('dinner');

// The time picker (24-hour "HH:MM" storage, 12-hour display) lives in
// ./TimeField so the scheduled-change forms use the same one.

// --- Date helpers --------------------------------------------------------
// Dates are stored as DD-MM-YYYY text (app convention); native <input type="date">
// uses YYYY-MM-DD, so convert in both directions, same as AddPrivateCharter.
const toDisplayDate = (yyyymmdd) => {
  if (!yyyymmdd) return '';
  const [y, m, d] = yyyymmdd.split('-');
  return `${d}-${m}-${y}`;
};
// DD-MM-YYYY text → comparable YYYYMMDD integer (real calendar order, NOT string order).
const dmyToNum = (ddmmyyyy) => {
  if (!ddmmyyyy) return 0;
  const [d, m, y] = ddmmyyyy.split('-').map(Number);
  if (Number.isNaN(d) || Number.isNaN(m) || Number.isNaN(y)) return 0;
  return y * 10000 + m * 100 + d;
};
const todayNum = () => {
  const n = new Date();
  return n.getFullYear() * 10000 + (n.getMonth() + 1) * 100 + n.getDate();
};

const EMPTY_FORM = {
  cruise_name: '', cruise_code: '', departure_time: '', end_time: '',
  reporting_time: '', online_booking_closes_at: '', duration_minutes: '', is_active: true, sort_order: '',
  has_upper_deck: true, has_middle_deck: true, capacity_alert_threshold: 80,
  dinner_adult_cruise_charge: 0, dinner_adult_food_charge: 0,
  dinner_child_cruise_charge: 0, dinner_child_food_charge: 0,
};

const iStyle = {
  width: '100%', background: 'var(--color-sunken)', border: '1.5px solid var(--color-border-strong)',
  borderRadius: 8, padding: '9px 12px', fontSize: '0.88rem', color: NAVY,
  outline: 'none', boxSizing: 'border-box',
};
const lStyle = { fontSize: '0.78rem', fontWeight: 700, color: 'var(--color-ink-secondary)', marginBottom: 5, display: 'block' };
const fg = { marginBottom: 14 };

// Click-to-pick time field. Stores/returns 24-hour "HH:MM"; shows 12-hour AM/PM.
function CruiseModal({ cruise, onSave, onClose }) {
  const isEdit = !!cruise;
  const [form, setForm] = useState(isEdit ? {
    cruise_name:                  cruise.cruise_name || '',
    cruise_code:                  cruise.cruise_code || '',
    departure_time:               cruise.departure_time || '',
    end_time:                     cruise.end_time || '',
    reporting_time:               cruise.reporting_time || '',
    online_booking_closes_at:     cruise.online_booking_closes_at || '',
    duration_minutes:             cruise.duration_minutes || '',
    is_active:                    cruise.is_active ?? true,
    sort_order:                   cruise.sort_order ?? '',
    has_upper_deck:               cruise.has_upper_deck ?? true,
    has_middle_deck:              cruise.has_middle_deck ?? true,
    capacity_alert_threshold:     cruise.capacity_alert_threshold ?? 80,
    dinner_adult_cruise_charge:   cruise.dinner_adult_cruise_charge ?? 0,
    dinner_adult_food_charge:     cruise.dinner_adult_food_charge ?? 0,
    dinner_child_cruise_charge:   cruise.dinner_child_cruise_charge ?? 0,
    dinner_child_food_charge:     cruise.dinner_child_food_charge ?? 0,
  } : { ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const dinnerMode = isDinner(form.cruise_name);

  const handleSave = async () => {
    if (!form.cruise_name.trim() || !form.cruise_code.trim()) {
      setError('Cruise name and code are required.');
      return;
    }
    const code = form.cruise_code.trim().toUpperCase();
    if (code.length < 2 || code.length > 4) {
      setError('Cruise code must be 2–4 characters.');
      return;
    }
    setSaving(true);
    setError('');
    const payload = {
      cruise_name:                  form.cruise_name.trim(),
      cruise_code:                  code,
      departure_time:               form.departure_time || null,
      end_time:                     form.end_time || null,
      reporting_time:               form.reporting_time || null,
      online_booking_closes_at:     form.online_booking_closes_at || null,
      duration_minutes:             form.duration_minutes ? Number(form.duration_minutes) : null,
      is_active:                    form.is_active,
      sort_order:                   form.sort_order !== '' ? Number(form.sort_order) : 0,
      has_upper_deck:               form.has_upper_deck,
      has_middle_deck:              form.has_middle_deck,
      capacity_alert_threshold:     Number(form.capacity_alert_threshold) || 80,
      dinner_adult_cruise_charge:   Number(form.dinner_adult_cruise_charge) || 0,
      dinner_adult_food_charge:     Number(form.dinner_adult_food_charge) || 0,
      dinner_child_cruise_charge:   Number(form.dinner_child_cruise_charge) || 0,
      dinner_child_food_charge:     Number(form.dinner_child_food_charge) || 0,
    };
    const res = isEdit
      ? await supabase.from('cruise_settings').update(payload).eq('id', cruise.id)
      : await supabase.from('cruise_settings').insert(payload);
    setSaving(false);
    if (res.error) { setError(res.error.message); return; }
    invalidateCruiseCache();
    onSave();
  };

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16, overflowY: 'auto' }}>
      <div style={{ background: '#fff', borderRadius: 18, padding: '28px 28px 24px', width: '100%', maxWidth: 760, boxShadow: '0 8px 32px rgba(10,22,40,0.22)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 800, color: NAVY, margin: 0 }}>
            {isEdit ? 'Edit Cruise' : 'Add Cruise'}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: 'var(--color-ink-muted)', padding: '0 4px', lineHeight: 1 }}>×</button>
        </div>

        {error && <div className="toast toast-error" style={{ marginBottom: 16 }}>{error}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0 14px' }}>
          <div style={fg}>
            <label style={lStyle}>Cruise Name *</label>
            <input style={iStyle} value={form.cruise_name} onChange={e => set('cruise_name', e.target.value)} placeholder="e.g. Sunset Cruise" />
          </div>
          <div style={fg}>
            <label style={lStyle}>Code * (2–4 chars)</label>
            <input style={iStyle} value={form.cruise_code} onChange={e => set('cruise_code', e.target.value.toUpperCase())} placeholder="SS" maxLength={4} />
          </div>
        </div>

        <p style={{ fontSize: '0.74rem', color: 'var(--color-ink-muted)', margin: '-4px 0 12px', lineHeight: 1.45 }}>
          These are the standing times and apply at once. To change times from a date without touching tickets already issued, use <strong>Schedule a change</strong> on the cruise card instead.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0 14px' }}>
          <div style={fg}>
            <label style={lStyle}>Departure</label>
            <TimeField value={form.departure_time} onChange={v => set('departure_time', v)} />
          </div>
          <div style={fg}>
            <label style={lStyle}>End Time</label>
            <TimeField value={form.end_time} onChange={v => set('end_time', v)} />
          </div>
          <div style={fg}>
            <label style={lStyle}>Reporting</label>
            <TimeField value={form.reporting_time} onChange={v => set('reporting_time', v)} />
          </div>
          <div style={fg}>
            <label style={lStyle}>Online Closes</label>
            <TimeField value={form.online_booking_closes_at} onChange={v => set('online_booking_closes_at', v)} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 14px' }}>
          <div style={fg}>
            <label style={lStyle}>Duration (mins)</label>
            <input style={iStyle} type="number" min="0" value={form.duration_minutes} onChange={e => set('duration_minutes', e.target.value)} placeholder="120" />
          </div>
          <div style={fg}>
            <label style={lStyle}>Sort Order</label>
            <input style={iStyle} type="number" min="0" value={form.sort_order} onChange={e => set('sort_order', e.target.value)} placeholder="1" />
          </div>
          <div style={fg}>
            <label style={lStyle}>Capacity Alert (%)</label>
            <input style={iStyle} type="number" min="0" max="100" value={form.capacity_alert_threshold} onChange={e => set('capacity_alert_threshold', e.target.value)} placeholder="80" />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 20, marginBottom: 16, flexWrap: 'wrap' }}>
          {[
            { key: 'has_upper_deck',  label: 'Has Upper Deck' },
            { key: 'has_middle_deck', label: 'Has Middle Deck' },
            { key: 'is_active',       label: 'Active' },
          ].map(({ key, label }) => (
            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: '#3a2a18' }}>
              <input type="checkbox" checked={!!form[key]} onChange={e => set(key, e.target.checked)}
                style={{ width: 16, height: 16, accentColor: GOLD, cursor: 'pointer' }} />
              {label}
            </label>
          ))}
        </div>

        {dinnerMode && (
          <>
            <div style={{ height: 1, background: 'var(--color-border)', margin: '2px 0 18px' }} />
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 14 }}>
              Dinner Charge Splits (per person, incl. GST)
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
              <div style={fg}>
                <label style={lStyle}>Adult Cruise Charge (₹)</label>
                <input style={iStyle} type="number" min="0" value={form.dinner_adult_cruise_charge} onChange={e => set('dinner_adult_cruise_charge', e.target.value)} />
              </div>
              <div style={fg}>
                <label style={lStyle}>Adult Food Charge (₹)</label>
                <input style={iStyle} type="number" min="0" value={form.dinner_adult_food_charge} onChange={e => set('dinner_adult_food_charge', e.target.value)} />
              </div>
              <div style={fg}>
                <label style={lStyle}>Child Cruise Charge (₹)</label>
                <input style={iStyle} type="number" min="0" value={form.dinner_child_cruise_charge} onChange={e => set('dinner_child_cruise_charge', e.target.value)} />
              </div>
              <div style={fg}>
                <label style={lStyle}>Child Food Charge (₹)</label>
                <input style={iStyle} type="number" min="0" value={form.dinner_child_food_charge} onChange={e => set('dinner_child_food_charge', e.target.value)} />
              </div>
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <button onClick={handleSave} disabled={saving}
            style={{ flex: 1, background: GOLD, color: '#fff', border: 'none', borderRadius: 9, padding: '12px 0', fontSize: '0.92rem', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add Cruise')}
          </button>
          <button onClick={onClose}
            style={{ flex: 1, background: 'none', border: '1.5px solid var(--color-border-strong)', borderRadius: 9, padding: '12px 0', fontSize: '0.92rem', fontWeight: 600, cursor: 'pointer', color: 'var(--color-ink-secondary)' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function PriceModal({ row, onSave, onClose }) {
  const [price, setPrice]   = useState(String(row.price));
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const handleSave = async () => {
    const p = parseInt(price, 10);
    if (isNaN(p) || p < 0) { setError('Enter a valid price.'); return; }
    setSaving(true);
    const { error: err } = await supabase.from('pricing').update({ price: p }).eq('id', row.id);
    setSaving(false);
    if (err) { setError(err.message); return; }
    onSave();
  };

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: '22px 24px', maxWidth: 300, width: '100%', boxShadow: '0 8px 32px rgba(10,22,40,0.22)' }}>
        <div style={{ fontWeight: 800, fontSize: '1rem', color: NAVY, marginBottom: 4 }}>Edit Price</div>
        <div style={{ fontSize: '0.78rem', color: 'var(--color-ink-muted)', marginBottom: 16 }}>
          {row.cruise_type} · {row.deck} · {row.passenger_type}
        </div>
        <div style={fg}>
          <label style={lStyle}>Price (₹)</label>
          <input
            type="number" min="0" value={price}
            onChange={e => setPrice(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
            style={iStyle} autoFocus
          />
        </div>
        {error && <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '-8px 0 12px' }}>{error}</p>}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={handleSave} disabled={saving}
            style={{ flex: 1, background: GOLD, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 0', fontWeight: 700, cursor: 'pointer', fontSize: '0.88rem' }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={onClose}
            style={{ flex: 1, background: 'none', border: '1.5px solid var(--color-border-strong)', borderRadius: 8, padding: '10px 0', fontWeight: 600, cursor: 'pointer', color: 'var(--color-ink-secondary)', fontSize: '0.88rem' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CruiseSettings({ onNavigate }) {
  const [cruises, setCruises]         = useState([]);
  const [inForce, setInForce]         = useState({}); // cruise_name → times in force today (cruise_details_on)
  const [pricingMap, setPricingMap]   = useState({});
  const [tables, setTables]           = useState([]);
  const [savingTableId, setSavingTableId] = useState(null);
  const [closures, setClosures]       = useState([]);
  const [closureForm, setClosureForm] = useState({ cruise_code: '', deck: '', dateInput: '', reason: '' });
  const [addingClosure, setAddingClosure] = useState(false);
  const [loading, setLoading]         = useState(true);
  const [modal, setModal]             = useState(null);
  const [priceModal, setPriceModal]   = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [toast, setToast]             = useState('');

  const load = async () => {
    setLoading(true);
    const [{ data: cs }, { data: pricing }, { data: ti }, { data: cl }, { data: today }] = await Promise.all([
      supabase.from('cruise_settings').select('*').order('sort_order'),
      supabase.from('pricing').select('*').order('cruise_type').order('deck').order('passenger_type'),
      supabase.from('table_inventory').select('*').order('deck').order('sort_order'),
      supabase.from('online_booking_closures').select('*'),
      supabase.rpc('cruise_details_on', { p_date: todayIso() }),
    ]);
    setCruises(cs || []);
    const force = {};
    (today || []).forEach(r => { force[r.cruise_name] = r; });
    setInForce(force);
    setTables(ti || []);
    setClosures(cl || []);
    const map = {};
    (pricing || []).forEach(r => {
      if (!map[r.cruise_type]) map[r.cruise_type] = [];
      map[r.cruise_type].push(r);
    });
    setPricingMap(map);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const handleSaved = () => { setModal(null); load(); showToast('Cruise saved.'); };
  const handlePriceSaved = () => { setPriceModal(null); load(); showToast('Price updated.'); };

  const handleToggleActive = async (c) => {
    await supabase.from('cruise_settings').update({ is_active: !c.is_active }).eq('id', c.id);
    invalidateCruiseCache();
    load();
  };

  const handleToggleBookable = async (t) => {
    const next = !t.is_online_bookable;
    setSavingTableId(t.id);
    setTables(prev => prev.map(x => x.id === t.id ? { ...x, is_online_bookable: next } : x));
    const { error } = await supabase.from('table_inventory').update({ is_online_bookable: next }).eq('id', t.id);
    setSavingTableId(null);
    if (error) {
      setTables(prev => prev.map(x => x.id === t.id ? { ...x, is_online_bookable: !next } : x));
      showToast('Could not update table.');
      return;
    }
    showToast(next ? `${t.table_name} is now bookable online.` : `${t.table_name} reserved from online booking.`);
  };

  const handleAddClosure = async () => {
    if (!closureForm.cruise_code || !closureForm.dateInput) {
      showToast('Pick a cruise and a date.');
      return;
    }
    setAddingClosure(true);
    const { error } = await supabase.from('online_booking_closures').insert({
      cruise_code: closureForm.cruise_code,
      date:        toDisplayDate(closureForm.dateInput),   // DD-MM-YYYY text
      deck:        closureForm.deck || null,               // NULL = whole cruise
      reason:      closureForm.reason.trim() || null,
    });
    setAddingClosure(false);
    if (error) { showToast(error.message); return; }
    setClosureForm({ cruise_code: '', deck: '', dateInput: '', reason: '' });
    load();
    showToast('Closure added.');
  };

  const handleRemoveClosure = async (cl) => {
    const { error } = await supabase.from('online_booking_closures').delete().eq('id', cl.id);
    if (error) { showToast(error.message); return; }
    load();
    showToast('Closure removed.');
  };

  const handleDelete = async (c) => {
    await supabase.from('cruise_settings').delete().eq('id', c.id);
    invalidateCruiseCache();
    setDeleteConfirm(null);
    load();
    showToast('Cruise deleted.');
  };

  // Group tables by their live deck value (nothing hardcoded) — preserves query order.
  const deckOrder = [];
  const tablesByDeck = {};
  tables.forEach(t => {
    if (!tablesByDeck[t.deck]) { tablesByDeck[t.deck] = []; deckOrder.push(t.deck); }
    tablesByDeck[t.deck].push(t);
  });

  // --- Online booking closures (derived) ---------------------------------
  const cruiseByCode = {};
  cruises.forEach(c => { cruiseByCode[c.cruise_code] = c; });

  // Live deck names (from table_inventory) that the given cruise actually has,
  // matched to its has_upper_deck / has_middle_deck flags from cruise_settings.
  const decksForCruise = (cruise) => {
    if (!cruise) return [];
    return deckOrder.filter(d => {
      const k = d.toLowerCase();
      if (k.includes('upper'))  return !!cruise.has_upper_deck;
      if (k.includes('middle')) return !!cruise.has_middle_deck;
      return false;
    });
  };
  const selectedClosureCruise = cruiseByCode[closureForm.cruise_code];
  const closureDeckOptions = decksForCruise(selectedClosureCruise);

  // Upcoming only (today or later), soonest-first — by REAL parsed date, not text order.
  const today = todayNum();
  const upcomingClosures = closures
    .filter(cl => dmyToNum(cl.date) >= today)
    .sort((a, b) => dmyToNum(a.date) - dmyToNum(b.date));
  const nowD = new Date();
  const todayInputStr = `${nowD.getFullYear()}-${pad2(nowD.getMonth() + 1)}-${pad2(nowD.getDate())}`;

  return (
    <div>
      {modal && (
        <CruiseModal
          cruise={modal === 'add' ? null : modal.cruise}
          onSave={handleSaved}
          onClose={() => setModal(null)}
        />
      )}
      {priceModal && (
        <PriceModal
          row={priceModal.row}
          onSave={handlePriceSaved}
          onClose={() => setPriceModal(null)}
        />
      )}
      {deleteConfirm && (
        <div onClick={e => { if (e.target === e.currentTarget) setDeleteConfirm(null); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: '24px 28px', maxWidth: 360, width: '100%', boxShadow: '0 8px 32px rgba(10,22,40,0.2)' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 800, color: NAVY, marginBottom: 10 }}>Delete "{deleteConfirm.cruise_name}"?</h3>
            <p style={{ fontSize: '0.83rem', color: 'var(--color-ink-secondary)', marginBottom: 20 }}>This cannot be undone. Existing bookings will not be affected, but this cruise will no longer appear as an option.</p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => handleDelete(deleteConfirm)}
                style={{ flex: 1, background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 0', fontWeight: 700, cursor: 'pointer', fontSize: '0.88rem' }}>
                Delete
              </button>
              <button onClick={() => setDeleteConfirm(null)}
                style={{ flex: 1, background: 'none', border: '1.5px solid var(--color-border-strong)', borderRadius: 8, padding: '10px 0', fontWeight: 600, cursor: 'pointer', color: 'var(--color-ink-secondary)', fontSize: '0.88rem' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="back-row">
        <button className="back-btn" onClick={() => onNavigate('settings')}>← Settings</button>
      </div>

      {toast && <div className="toast toast-success" style={{ marginBottom: 12 }}>{toast}</div>}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 800, color: NAVY, marginBottom: 4 }}>Cruises & Pricing</h2>
          <p style={{ fontSize: '0.78rem', color: 'var(--color-ink-muted)' }}>Configure cruise types, timings, deck options, and per-passenger pricing</p>
        </div>
        <button className="btn btn-gold" onClick={() => setModal('add')}>+ Add Cruise</button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--color-ink-faint)', fontSize: '0.9rem' }}>Loading…</div>
      ) : cruises.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--color-ink-faint)', fontSize: '0.9rem' }}>No cruises configured yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {cruises.map(c => {
            const dinner = isDinner(c.cruise_name);
            const pRows  = pricingMap[c.cruise_name] || [];
            // Times shown are the ones in force TODAY (a scheduled change may
            // override the standing row); the standing row is the fallback.
            const t = inForce[c.cruise_name] || c;
            const scheduled = Boolean(inForce[c.cruise_name]?.times_scheduled);
            return (
              <div key={c.id} style={{
                background: '#fff', border: '1px solid var(--color-border)', borderRadius: 14,
                boxShadow: '0 1px 6px rgba(10,22,40,0.06)',
                opacity: c.is_active ? 1 : 0.55,
                overflow: 'hidden',
              }}>
                {/* Cruise header row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, padding: '16px 20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 1 }}>
                    <div style={{ background: NAVY, color: GOLD, borderRadius: 8, padding: '5px 12px', fontWeight: 800, fontSize: '0.9rem', letterSpacing: '1px', flexShrink: 0 }}>
                      {c.cruise_code}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.95rem', color: NAVY }}>{c.cruise_name}</div>
                      <div style={{ fontSize: '0.73rem', color: 'var(--color-ink-muted)', marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        {t.departure_time && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Clock size={13} strokeWidth={1.75} aria-hidden="true" /> {t.departure_time}–{t.end_time}{scheduled ? ' (scheduled)' : ''}</span>}
                        {t.reporting_time && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><ClipboardList size={13} strokeWidth={1.75} aria-hidden="true" /> Report: {t.reporting_time}</span>}
                        {c.online_booking_closes_at && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Globe size={13} strokeWidth={1.75} aria-hidden="true" /> Online closes: {c.online_booking_closes_at}</span>}
                        {c.capacity_alert_threshold && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><AlertTriangle size={13} strokeWidth={1.75} aria-hidden="true" /> Alert: {c.capacity_alert_threshold}%</span>}
                        {c.has_upper_deck  && <span>✓ Upper</span>}
                        {c.has_middle_deck && <span>✓ Middle</span>}
                        {!c.is_active && <span style={{ color: '#dc2626', fontWeight: 700 }}>INACTIVE</span>}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button className="btn btn-sm btn-outline" onClick={() => handleToggleActive(c)}>
                      {c.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button className="btn btn-sm btn-outline" onClick={() => setModal({ cruise: c })}>Edit</button>
                    <button className="btn btn-sm btn-danger" onClick={() => setDeleteConfirm(c)}>Delete</button>
                  </div>
                </div>

                {/* Scheduled time changes — from a date, without touching the standing row */}
                <CruiseTimesSchedule cruise={c} current={t} onChanged={load} />

                {/* Pricing section */}
                {dinner ? (
                  <div style={{ borderTop: '1px solid var(--color-border-subtle)', padding: '12px 20px', background: 'var(--color-canvas)' }}>
                    <div style={{ fontSize: '0.69rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                      Dinner Charge Splits (per person, incl. GST)
                    </div>
                    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                      {[
                        ['Adult Cruise', c.dinner_adult_cruise_charge],
                        ['Adult Food',   c.dinner_adult_food_charge],
                        ['Child Cruise', c.dinner_child_cruise_charge],
                        ['Child Food',   c.dinner_child_food_charge],
                      ].map(([label, val]) => (
                        <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <span style={{ fontSize: '0.68rem', color: 'var(--color-ink-muted)', fontWeight: 600 }}>{label}</span>
                          <span style={{ fontSize: '0.88rem', fontWeight: 700, color: NAVY }}>
                            ₹{Number(val || 0).toLocaleString('en-IN')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : pRows.length > 0 ? (
                  <div style={{ borderTop: '1px solid var(--color-border-subtle)', background: 'var(--color-canvas)' }}>
                    <div style={{ display: 'flex', padding: '6px 20px', borderBottom: '1px solid var(--color-border-subtle)' }}>
                      <div style={{ flex: 1, fontSize: '0.68rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Deck</div>
                      <div style={{ flex: 1, fontSize: '0.68rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Passenger</div>
                      <div style={{ minWidth: 80, fontSize: '0.68rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'right' }}>Price</div>
                      <div style={{ width: 56 }} />
                    </div>
                    {pRows.map((row, idx) => (
                      <div key={row.id} style={{ display: 'flex', alignItems: 'center', padding: '9px 20px', borderBottom: idx < pRows.length - 1 ? '1px solid var(--color-border-subtle)' : 'none' }}>
                        <div style={{ flex: 1, fontSize: '0.85rem', color: 'var(--color-ink)' }}>{row.deck}</div>
                        <div style={{ flex: 1, fontSize: '0.85rem', color: 'var(--color-ink)' }}>{row.passenger_type}</div>
                        <div style={{ minWidth: 80, fontWeight: 700, fontSize: '0.9rem', color: NAVY, textAlign: 'right' }}>
                          ₹{Number(row.price).toLocaleString('en-IN')}
                        </div>
                        <div style={{ width: 56, display: 'flex', justifyContent: 'flex-end' }}>
                          <button className="btn btn-outline btn-sm" onClick={() => setPriceModal({ row })}>Edit</button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {/* Online table reservations — GLOBAL across all cruises, grouped by live deck value */}
      {!loading && tables.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <div style={{ marginBottom: 14 }}>
            <h2 style={{ fontSize: '1.2rem', fontWeight: 800, color: NAVY, marginBottom: 4 }}>Online Table Reservations</h2>
            <p style={{ fontSize: '0.78rem', color: 'var(--color-ink-muted)' }}>
              Choose which tables can be booked online. Uncheck a table to reserve it from online booking.
            </p>
          </div>

          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 8,
            background: 'var(--color-canvas)', border: '1px solid var(--color-border)',
            borderRadius: 10, padding: '10px 14px', marginBottom: 14,
          }}>
            <AlertTriangle size={15} strokeWidth={1.75} aria-hidden="true" style={{ color: GOLD, flexShrink: 0, marginTop: 1 }} />
            <p style={{ fontSize: '0.78rem', color: 'var(--color-ink-secondary)', margin: 0, lineHeight: 1.45 }}>
              These settings are <strong>global</strong>. A table reserved here is reserved across <strong>every cruise that uses that deck</strong> — this is not a per-cruise setting.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {deckOrder.map(deck => {
              const deckTables = tablesByDeck[deck];
              const bookableCount = deckTables.filter(t => t.is_online_bookable).length;
              return (
                <div key={deck} style={{
                  background: '#fff', border: '1px solid var(--color-border)', borderRadius: 14,
                  boxShadow: '0 1px 6px rgba(10,22,40,0.06)', overflow: 'hidden',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, padding: '12px 20px', borderBottom: '1px solid var(--color-border-subtle)', background: 'var(--color-canvas)' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.9rem', color: NAVY }}>{deck}</div>
                    <div style={{ fontSize: '0.73rem', color: 'var(--color-ink-muted)' }}>
                      {bookableCount} of {deckTables.length} bookable online
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '14px 20px' }}>
                    {deckTables.map(t => {
                      const on = !!t.is_online_bookable;
                      return (
                        <label key={t.id} title={on ? 'Bookable online — uncheck to reserve' : 'Reserved from online booking'}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                            border: '1px solid var(--color-border-strong)', borderRadius: 8,
                            padding: '7px 12px', fontSize: '0.85rem', fontWeight: 600,
                            color: on ? 'var(--color-ink)' : 'var(--color-ink-muted)',
                            background: on ? '#fff' : 'var(--color-sunken)',
                            opacity: savingTableId === t.id ? 0.6 : 1,
                          }}>
                          <input type="checkbox" checked={on}
                            disabled={savingTableId === t.id}
                            onChange={() => handleToggleBookable(t)}
                            style={{ width: 16, height: 16, accentColor: GOLD, cursor: 'pointer' }} />
                          <span>{t.table_name}</span>
                          {!on && <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Reserved</span>}
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Online booking closures — close online booking for a cruise (or one deck) on a date */}
      {!loading && (
        <div style={{ marginTop: 28 }}>
          <div style={{ marginBottom: 14 }}>
            <h2 style={{ fontSize: '1.2rem', fontWeight: 800, color: NAVY, marginBottom: 4 }}>Online Booking Closures</h2>
            <p style={{ fontSize: '0.78rem', color: 'var(--color-ink-muted)' }}>
              Close online booking for a cruise — or a single deck of it — on a specific date (private charters, sell-outs). This does not affect the seating sheet.
            </p>
          </div>

          {/* Add a closure */}
          <div style={{
            background: '#fff', border: '1px solid var(--color-border)', borderRadius: 14,
            boxShadow: '0 1px 6px rgba(10,22,40,0.06)', padding: '16px 20px', marginBottom: 14,
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.2fr 1fr 1.4fr', gap: '0 14px' }}>
              <div style={fg}>
                <label style={lStyle}>Cruise</label>
                <select style={{ ...iStyle, cursor: 'pointer' }} value={closureForm.cruise_code}
                  onChange={e => setClosureForm(p => ({ ...p, cruise_code: e.target.value, deck: '' }))}>
                  <option value="">Select cruise…</option>
                  {cruises.map(c => (
                    <option key={c.id} value={c.cruise_code}>{c.cruise_name}</option>
                  ))}
                </select>
              </div>
              <div style={fg}>
                <label style={lStyle}>Deck</label>
                <select style={{ ...iStyle, cursor: selectedClosureCruise ? 'pointer' : 'not-allowed' }}
                  value={closureForm.deck} disabled={!selectedClosureCruise}
                  onChange={e => setClosureForm(p => ({ ...p, deck: e.target.value }))}>
                  <option value="">Whole cruise</option>
                  {closureDeckOptions.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
              <div style={fg}>
                <label style={lStyle}>Date</label>
                <input style={iStyle} type="date" min={todayInputStr} value={closureForm.dateInput}
                  onChange={e => setClosureForm(p => ({ ...p, dateInput: e.target.value }))} />
              </div>
              <div style={fg}>
                <label style={lStyle}>Reason (optional)</label>
                <input style={iStyle} type="text" value={closureForm.reason} maxLength={120}
                  placeholder="e.g. Private charter"
                  onChange={e => setClosureForm(p => ({ ...p, reason: e.target.value }))} />
              </div>
            </div>
            <button onClick={handleAddClosure} disabled={addingClosure}
              style={{ background: GOLD, color: '#fff', border: 'none', borderRadius: 9, padding: '10px 20px', fontSize: '0.88rem', fontWeight: 700, cursor: addingClosure ? 'not-allowed' : 'pointer', opacity: addingClosure ? 0.7 : 1 }}>
              {addingClosure ? 'Adding…' : 'Add closure'}
            </button>
          </div>

          {/* Upcoming closures */}
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
            Upcoming closures
          </div>
          {upcomingClosures.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--color-ink-faint)', fontSize: '0.85rem', background: 'var(--color-canvas)', border: '1px solid var(--color-border)', borderRadius: 12 }}>
              No upcoming closures.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {upcomingClosures.map(cl => {
                const cruise = cruiseByCode[cl.cruise_code];
                return (
                  <div key={cl.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10,
                    background: '#fff', border: '1px solid var(--color-border)', borderRadius: 12,
                    boxShadow: '0 1px 6px rgba(10,22,40,0.06)', padding: '12px 18px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 1, flexWrap: 'wrap' }}>
                      <div style={{ background: NAVY, color: GOLD, borderRadius: 8, padding: '4px 10px', fontWeight: 800, fontSize: '0.82rem', letterSpacing: '1px', flexShrink: 0 }}>
                        {cl.cruise_code}
                      </div>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem', color: NAVY }}>
                        {cruise ? cruise.cruise_name : cl.cruise_code}
                      </div>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.8rem', color: 'var(--color-ink-secondary)', fontWeight: 600 }}>
                        <Clock size={13} strokeWidth={1.75} aria-hidden="true" /> {cl.date}
                      </span>
                      <span style={{ fontSize: '0.78rem', fontWeight: 700, color: cl.deck ? NAVY : 'var(--color-ink-muted)', background: 'var(--color-canvas)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '3px 9px' }}>
                        {cl.deck || 'Whole cruise'}
                      </span>
                      {cl.reason && <span style={{ fontSize: '0.78rem', color: 'var(--color-ink-muted)' }}>{cl.reason}</span>}
                    </div>
                    <button className="btn btn-sm btn-danger" onClick={() => handleRemoveClosure(cl)}>Remove</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
